import { z } from 'zod';

import { api } from './client';

const IssuedTicketSchema = z.object({
  ticket: z.string().min(1),
  expiresInSeconds: z.number(),
});

export type IssuedTicket = z.infer<typeof IssuedTicketSchema>;

/**
 * Mints a single-use ticket for the SSE stream.
 *
 * `EventSource` cannot send an Authorization header -- but that is the smaller half of
 * the problem. Clerk session tokens expire in 60 seconds, so even a header-capable client
 * could not hold one open across a 30-minute stream, and there is no way to refresh a
 * header on a live connection. The stream credential has to be decoupled from the session
 * token regardless of what the browser API allows.
 */
export async function issueStreamTicket(signal?: AbortSignal): Promise<IssuedTicket> {
  const { data } = await api.post('/api/v1/stream/ticket', null, { signal });
  return IssuedTicketSchema.parse(data);
}

/**
 * One frame off the wire.
 *
 * Parsed rather than trusted: a malformed frame must drop on the floor without killing
 * the stream. One bad payload taking the whole fleet map offline until a reload is a far
 * worse failure than one missing fix.
 */
export const PositionFixFrame = z.object({
  id: z.number(),
  trackerId: z.string(),
  lat: z.number(),
  lon: z.number(),
  altitudeM: z.number().nullish(),
  speedKmh: z.number().nullish(),
  headingDeg: z.number().nullish(),
  satellites: z.number().nullish(),
  hdop: z.number().nullish(),
  accuracyM: z.number().nullish(),
  // An unknown future source degrades to "absent" rather than dropping the frame.
  source: z.enum(['GNSS', 'CELL']).nullish().catch(null),
  batteryMv: z.number().nullish(),
  batteryPct: z.number().nullish(),
  deviceTime: z.boolean(),
  recordedAt: z.string(),
  receivedAt: z.string(),
});

/** The server may coalesce several fixes into one frame. */
export const PositionFramePayload = z.union([PositionFixFrame, z.array(PositionFixFrame)]);
